import { useState } from 'react';
import Reveal from '@/components/base/Reveal';
import { trainingPrograms } from '@/mocks/formation';

type ProgramExplorerProps = {
  activeSlug: string;
  onChange: (slug: string) => void;
  onRegister: (programTitle: string) => void;
};

export default function ProgramExplorer({ activeSlug, onChange, onRegister }: ProgramExplorerProps) {
  const [openModule, setOpenModule] = useState(0);
  const active = trainingPrograms.find((program) => program.slug === activeSlug) ?? trainingPrograms[0];

  const meta = [
    { icon: 'ri-time-line', label: 'Durée', value: active.duration },
    { icon: 'ri-calendar-schedule-line', label: 'Rythme', value: active.rhythm },
    { icon: 'ri-bar-chart-line', label: 'Niveau', value: active.level },
    { icon: 'ri-group-line', label: 'Effectif', value: active.groupSize },
  ];

  return (
    <div className="grid gap-8 lg:grid-cols-[0.72fr_1.28fr] lg:gap-12">
      {/* Program list */}
      <div>
        <p className="label-mono text-primary-600">Les parcours</p>
        <div className="mt-4 flex flex-col gap-2">
          {trainingPrograms.map((program) => {
            const isActive = program.slug === active.slug;
            return (
              <button
                key={program.slug}
                type="button"
                onClick={() => {
                  onChange(program.slug);
                  setOpenModule(0);
                }}
                aria-pressed={isActive}
                className={`group flex cursor-pointer items-start gap-4 rounded-lg border px-4 py-4 text-left transition-colors duration-300 ${
                  isActive
                    ? 'border-primary-500 bg-foreground-950 text-background-50'
                    : 'border-background-200 bg-background-50 text-foreground-900 hover:border-primary-300 hover:bg-background-100'
                }`}
              >
                <span
                  className={`font-label mt-1 text-[10px] tracking-[0.18em] ${
                    isActive ? 'text-accent-400' : 'text-primary-600'
                  }`}
                >
                  {program.index}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-heading text-lg font-semibold uppercase leading-tight">
                    {program.title}
                  </span>
                  <span
                    className={`mt-1 block text-[11px] ${
                      isActive ? 'text-background-400' : 'text-foreground-500'
                    }`}
                  >
                    {program.duration}
                  </span>
                </span>
                <i
                  className={`ri-arrow-right-s-line mt-1 text-lg transition-transform duration-300 ${
                    isActive ? 'text-accent-400' : 'text-foreground-400 group-hover:translate-x-1'
                  }`}
                />
              </button>
            );
          })}
        </div>

        <div className="mt-5 rounded-lg border border-accent-200 bg-accent-50 p-5">
          <div className="flex items-center gap-2">
            <i className="ri-award-line text-accent-700" />
            <p className="label-mono text-accent-800">Certification</p>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-accent-900">{active.certification}</p>
        </div>
      </div>

      {/* Active program detail */}
      <Reveal key={active.slug} delay={60}>
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-100/50">
          <div className="relative h-[220px] w-full overflow-hidden bg-background-200 md:h-[280px]">
            <img
              src={active.image}
              alt={active.title}
              title={`${active.title} — formation SGSCTIC Douala`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/30 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
              <span className="font-label text-[10px] uppercase tracking-[0.2em] text-accent-400">
                Parcours {active.index}
              </span>
              <h3 className="display-title mt-2 text-3xl text-background-50 md:text-4xl">
                {active.title}
              </h3>
            </div>
          </div>

          <div className="p-5 md:p-7">
            <p className="text-sm leading-relaxed text-foreground-700">{active.tagline}</p>

            <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-md bg-background-200">
              {meta.map((item) => (
                <div key={item.label} className="bg-background-50 px-3 py-3">
                  <p className="label-mono flex items-center gap-1.5 text-foreground-500">
                    <i className={`${item.icon} text-primary-500`} />
                    {item.label}
                  </p>
                  <p className="mt-1 text-xs font-semibold leading-snug text-foreground-950">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-7">
              <div className="flex items-center justify-between">
                <p className="label-mono text-primary-600">Programme détaillé</p>
                <span className="font-label text-[10px] uppercase tracking-[0.16em] text-foreground-400">
                  {active.modules.length} modules
                </span>
              </div>

              <div className="mt-3 divide-y divide-background-200 border-y border-background-200">
                {active.modules.map((module, index) => {
                  const isOpen = index === openModule;
                  return (
                    <div key={module.title}>
                      <button
                        type="button"
                        onClick={() => setOpenModule(isOpen ? -1 : index)}
                        aria-expanded={isOpen}
                        className="flex w-full cursor-pointer items-center justify-between gap-4 py-3.5 text-left"
                      >
                        <span className="flex min-w-0 items-baseline gap-3">
                          <span className="font-label text-[10px] tracking-[0.16em] text-primary-600">
                            {(index + 1).toString().padStart(2, '0')}
                          </span>
                          <span
                            className={`text-sm font-semibold leading-snug transition-colors ${
                              isOpen ? 'text-primary-700' : 'text-foreground-950'
                            }`}
                          >
                            {module.title}
                          </span>
                        </span>
                        <span className="flex flex-shrink-0 items-center gap-3">
                          <span className="whitespace-nowrap font-label text-[10px] text-foreground-500">
                            {module.hours}
                          </span>
                          <i
                            className={`ri-add-line text-base transition-transform duration-300 ${
                              isOpen ? 'rotate-45 text-primary-600' : 'text-foreground-400'
                            }`}
                          />
                        </span>
                      </button>
                      <div
                        className={`overflow-hidden transition-all duration-500 ${
                          isOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <p className="pb-4 pl-7 pr-2 text-xs leading-relaxed text-foreground-600">
                          {module.text}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-7">
              <p className="label-mono text-primary-600">À l’issue du parcours</p>
              <ul className="mt-3 grid gap-2.5 sm:grid-cols-2">
                {active.outcomes.map((outcome) => (
                  <li key={outcome} className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-500/10 text-primary-600">
                      <i className="ri-check-line text-xs" />
                    </span>
                    <span className="text-xs leading-relaxed text-foreground-700">{outcome}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-7 flex flex-col gap-3 border-t border-background-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-foreground-500">
                Tarif et dates communiqués sur demande, selon le parcours.
              </p>
              <button
                type="button"
                onClick={() => onRegister(active.title)}
                className="btn-shine flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.12em] text-background-50 transition-colors hover:bg-primary-600"
              >
                S’inscrire à ce parcours
                <i className="ri-arrow-right-up-line text-sm" />
              </button>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  );
}